import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { FileText, ExternalLink, Plus, FileEdit } from "lucide-react";
import { Spinner } from "./CommonComponents";
import { useItemDetailsContext } from "./ItemDetailsContext";

const ItemDetailsDescriptionSection = () => {
    const {
        hasDescriptionGdoc,
        descriptionGdocUrl,
        isCreatingDescriptionGoogleDoc,
        isDocLoading,
        setIsDocLoading,
        handleCreateDescriptionGoogleDoc,
        handleOpenInNewTab,
    } = useItemDetailsContext();

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileEdit className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
                    {hasDescriptionGdoc && (
                        <Badge variant="secondary" className="text-xs">
                            Google Doc
                        </Badge>
                    )}
                </div>
                
                {isCreatingDescriptionGoogleDoc ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground animate-pulse">
                        <Spinner size={14} />
                        Creating Google Doc...
                    </div>
                ) : !hasDescriptionGdoc ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCreateDescriptionGoogleDoc}
                        className="gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Create Description Doc
                    </Button>
                ) : (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleOpenInNewTab}
                                className="gap-2"
                            >
                                Open in new tab
                                <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit in Google Docs</TooltipContent>
                    </Tooltip>
                )}
            </div>

            {!hasDescriptionGdoc ? (
                <Card className="border-dashed py-0">
                    <CardContent className="flex flex-col items-center justify-center py-10 px-6">
                        <div className="h-14 w-14 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                            <FileText className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <CardTitle className="text-base mb-2">No Description Yet</CardTitle>
                        <CardDescription className="text-center max-w-sm">
                            Create a Google Doc to add a rich description with formatting, images, and more.
                        </CardDescription>
                    </CardContent>
                </Card>
            ) : (
                <Card className="py-0 overflow-hidden">
                    <CardContent className="p-0 relative">
                        {isDocLoading && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/90 backdrop-blur-sm z-10 gap-3">
                                <Spinner size={24} />
                                <p className="text-sm text-muted-foreground">Loading document...</p>
                            </div>
                        )}
                        <iframe
                            src={descriptionGdocUrl}
                            className={cn(
                                "w-full h-[500px] border-0 transition-opacity duration-300",
                                isDocLoading && "opacity-30"
                            )}
                            onLoad={() => setIsDocLoading(false)}
                            title="Description Document"
                        />
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default ItemDetailsDescriptionSection;
